import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { isAthlete } from '../utils/roles';
import { canAccessAthleteDetail } from '../lib/athleteDetail';
import TemplatesRail from '../components/messaging/TemplatesRail';
import TemplateLibrary from '../components/messaging/TemplateLibrary';
import TemplateEditor from '../components/messaging/TemplateEditor';
import FullScreenImage from '../components/messaging/FullScreenImage';
import Button from '../components/Button';

import { RailFrame, usesRail } from '../components/AdminShell';
import ConversationContext, { TemplatesPanel } from '../components/messaging/ConversationContext';
import '../components/messaging/Messaging.css';

const TEMPLATE_ROLES = ['coach', 'basic_coach', 'internal_staff', 'admin'];

// Roles considered "Personal" for the conversation filter pills.
const PERSONAL_ROLES = ['coach', 'basic_coach', 'internal_staff', 'admin', 'partner'];

// Conversation list is paginated server-side (GET /conversations?limit&offset).
const CONV_PAGE_SIZE = 50;

// Merge a freshly-fetched page into the already-loaded list, de-duping by id
// (incoming wins) and keeping newest-first order. Lets us append pages on scroll
// and refresh page 1 on new messages without dropping loaded pages or duplicating.
function mergeConversations(existing, incoming) {
  const byId = new Map(existing.map(c => [c.id, c]));
  incoming.forEach(c => byId.set(c.id, c));
  return Array.from(byId.values()).sort((a, b) => {
    const ta = a.last_message_at || a.created_at || '';
    const tb = b.last_message_at || b.created_at || '';
    return tb.localeCompare(ta); // desc
  });
}

/* ── Helper utilities ──────────────────────────────────────────────────── */

function timeAgo(dateString) {
  if (!dateString) return '';
  const now = new Date();
  const date = new Date(dateString);
  const seconds = Math.floor((now - date) / 1000);
  if (seconds < 60) return 'Just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days}d`;
  return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

const AVATAR_COLORS = [
  '#e74c3c', '#e67e22', '#f1c40f', '#2ecc71', '#1abc9c',
  '#3498db', '#9b59b6', '#e84393', '#00cec9', '#6c5ce7',
];

function getAvatarColor(name) {
  if (!name) return AVATAR_COLORS[0];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function friendlyRole(role) {
  const map = { athlete: 'Athlete', player_parent: 'Athlete', base_user: 'Athlete', coach: 'Coach', admin: 'Admin', internal_staff: 'Staff', partner: 'Partner', guardian: 'Guardian' };
  return map[role] || role;
}

/* ── Component ─────────────────────────────────────────────────────────── */

export default function MessagingPage() {
  const { user, API_URL } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const MESSAGING_URL = `${API_URL.replace('/api', '')}/messaging`;

  const [conversations, setConversations] = useState([]);
  const [conversationsLoaded, setConversationsLoaded] = useState(false);
  const [convHasMore, setConvHasMore] = useState(false);
  const [convLoadingMore, setConvLoadingMore] = useState(false);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [wsConnected, setWsConnected] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [typingUser, setTypingUser] = useState(null);
  const [pendingTarget, setPendingTarget] = useState(null); // user selected from search, no conversation yet
  const [coachProfileModal, setCoachProfileModal] = useState(null);
  const [avatarPhotos, setAvatarPhotos] = useState({}); // { userId: url }
  const [flagModal, setFlagModal] = useState(null); // { messageId }
  const [flagReason, setFlagReason] = useState('');
  const [blockConfirm, setBlockConfirm] = useState(null); // { userId, name }
  const [menuMessageId, setMenuMessageId] = useState(null); // message id with open menu
  const [templateModal, setTemplateModal] = useState(null); // 'library' | 'new' | null
  const [templatesReloadKey, setTemplatesReloadKey] = useState(0);
  const [convFilter, setConvFilter] = useState('all'); // 'all' | 'unread' | 'personal' | 'newsletter'
  const [convSearchQuery, setConvSearchQuery] = useState('');
  const [convSearchResults, setConvSearchResults] = useState(null); // null = not searching
  const [fullScreenImage, setFullScreenImage] = useState(null); // proxied image URL
  const [pendingFile, setPendingFile] = useState(null); // { file, previewUrl }

  const wsRef = useRef(null);
  const messagesEndRef = useRef(null);
  const chatMessagesRef = useRef(null);
  const fileInputRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const lastTypingSentRef = useRef(0);
  const selectedConvRef = useRef(null);
  const convSearchSeqRef = useRef(0);
  const conversationsRef = useRef([]);
  const isSendingRef = useRef(false);
  // isSendingRef guards re-entrancy (setState is async and cannot); `sending` is
  // what actually re-renders the composer. Before this existed, a send gave the
  // user no visible feedback at all while the request was in flight.
  const [sending, setSending] = useState(false);
  // Admin / staff / partner get the three-card layout inside the rail; other roles keep the original.
  const railView = usesRail(user?.role);
  const [pickerOpen, setPickerOpen] = useState(false);       // the "new message" user search popover
  const [headerMenuOpen, setHeaderMenuOpen] = useState(false);
  const [templatesPop, setTemplatesPop] = useState(false);   // Templates popover in the composer (narrow widths)
  const loadingMoreConvsRef = useRef(false);
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimerRef = useRef(null);
  const wsShouldRunRef = useRef(true);

  // Keep ref in sync with state for WS handler
  useEffect(() => {
    selectedConvRef.current = selectedConversation;
  }, [selectedConversation]);

  // Keep a ref of the loaded conversations so the (debounced) conversation
  // search can merge is_muted without re-firing on every list change.
  useEffect(() => {
    conversationsRef.current = conversations;
  }, [conversations]);

  const canInitiate = user && !isAthlete(user.role);
  const canUseTemplates = user && TEMPLATE_ROLES.includes(user.role);
  // /athletes/:id is RoleProtectedRoute-gated to admin/internal_staff/coach/partner.
  // A non-privileged viewer (player_parent/guardian/base_user) who navigates there gets
  // bounced to /dashboard, ejecting them from the chat — so only viewers who can open
  // the page get the athlete-page navigation (and its "View Profile" button). This is
  // page-ACCESS (which includes coach, read-only), not verify capability.
  const canViewAthletePage = canAccessAthleteDetail(user?.role);

  const getAxiosConfig = () => ({
    headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
  });

  /** Build an authenticated proxy URL for an S3 attachment. */
  const proxyUrl = (rawUrl) => {
    if (!rawUrl) return rawUrl;
    const token = localStorage.getItem('token');
    return `${MESSAGING_URL}/attachments/proxy?url=${encodeURIComponent(rawUrl)}&token=${token}`;
  };

  // ── Load conversations ─────────────────────────────────────────────────

  // First page (also used to refresh on new messages). Merges into whatever is
  // already loaded so scrolled-in older pages aren't dropped on a WS refresh.
  const loadConversations = useCallback(async () => {
    try {
      const response = await axios.get(
        `${MESSAGING_URL}/conversations?limit=${CONV_PAGE_SIZE}&offset=0`,
        getAxiosConfig()
      );
      const items = response.data.conversations || [];
      setConversations(prev => (prev.length ? mergeConversations(prev, items) : items));
      setConvHasMore(items.length === CONV_PAGE_SIZE);
      setConversationsLoaded(true);
    } catch (error) {
      console.error('Error loading conversations:', error);
    }
  }, [MESSAGING_URL]);

  // Next page — appended when the user scrolls to the bottom of the list.
  const loadMoreConversations = useCallback(async () => {
    if (loadingMoreConvsRef.current) return;
    loadingMoreConvsRef.current = true;
    setConvLoadingMore(true);
    try {
      const offset = conversationsRef.current.length;
      const response = await axios.get(
        `${MESSAGING_URL}/conversations?limit=${CONV_PAGE_SIZE}&offset=${offset}`,
        getAxiosConfig()
      );
      const items = response.data.conversations || [];
      setConversations(prev => mergeConversations(prev, items));
      setConvHasMore(items.length === CONV_PAGE_SIZE);
    } catch (error) {
      console.error('Error loading more conversations:', error);
    } finally {
      loadingMoreConvsRef.current = false;
      setConvLoadingMore(false);
    }
  }, [MESSAGING_URL]);

  // ── Load messages for a conversation ───────────────────────────────────

  const loadMessages = useCallback(async (convId) => {
    try {
      const response = await axios.get(
        `${MESSAGING_URL}/conversations/${convId}/messages`,
        getAxiosConfig()
      );
      setMessages(response.data.messages || []);
    } catch (error) {
      console.error('Error loading messages:', error);
    }
  }, [MESSAGING_URL]);

  // ── WebSocket connection ───────────────────────────────────────────────

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;

    const MAX_RECONNECT_ATTEMPTS = 5;
    wsShouldRunRef.current = true;

    const scheduleReconnect = () => {
      if (!wsShouldRunRef.current) return;
      if (reconnectAttemptsRef.current >= MAX_RECONNECT_ATTEMPTS) return;
      if (reconnectTimerRef.current) return; // already scheduled
      const attempt = reconnectAttemptsRef.current; // 0-based
      const delay = Math.pow(2, attempt + 1) * 1000; // 2s, 4s, 8s, 16s, 32s
      reconnectAttemptsRef.current = attempt + 1;
      reconnectTimerRef.current = setTimeout(() => {
        reconnectTimerRef.current = null;
        connect();
      }, delay);
    };

    const connect = () => {
      if (!wsShouldRunRef.current) return;
      const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsUrl = `${wsProtocol}//${window.location.host}/messaging/ws?token=${token}`;
      const websocket = new WebSocket(wsUrl);
      wsRef.current = websocket;

    websocket.onopen = () => {
      wsRef.current = websocket;
      reconnectAttemptsRef.current = 0; // reset backoff on success
      setWsConnected(true);
    };

    websocket.onclose = () => {
      if (wsRef.current === websocket) wsRef.current = null;
      setWsConnected(false);
      scheduleReconnect();
    };

    websocket.onerror = () => {
      // onclose typically follows; ensure the socket is torn down and a reconnect is scheduled.
      try { websocket.close(); } catch (e) { /* noop */ }
    };

    websocket.onmessage = (event) => {
      const data = JSON.parse(event.data);

      if (data.type === 'new_message') {
        const currentConv = selectedConvRef.current;
        if (currentConv && data.conversation_id === currentConv.id) {
          setMessages(prev => [...prev, {
            id: data.id,
            sender_id: data.sender_id,
            sender_name: data.sender_name,
            content: data.content,
            attachment_url: data.attachment_url,
            attachment_type: data.attachment_type,
            is_mine: false,
            is_read: true,
            created_at: data.created_at
          }]);
          // Send mark_read since we're viewing this conversation
          if (websocket.readyState === WebSocket.OPEN) {
            websocket.send(JSON.stringify({
              type: 'mark_read',
              conversation_id: data.conversation_id
            }));
          }
        }
        loadConversations();
      } else if (data.type === 'sent') {
        setMessages(prev => [...prev, {
          id: data.id,
          content: data.content,
          attachment_url: data.attachment_url,
          attachment_type: data.attachment_type,
          is_mine: true,
          is_read: false,
          created_at: data.created_at
        }]);
        loadConversations();
      } else if (data.type === 'typing_indicator') {
        const currentConv = selectedConvRef.current;
        if (currentConv && data.conversation_id === currentConv.id) {
          setTypingUser(data.user_name);
          clearTimeout(typingTimeoutRef.current);
          typingTimeoutRef.current = setTimeout(() => setTypingUser(null), 3000);
        }
      } else if (data.type === 'read_receipt') {
        const currentConv = selectedConvRef.current;
        if (currentConv && data.conversation_id === currentConv.id) {
          setMessages(prev => prev.map(msg =>
            msg.is_mine ? { ...msg, is_read: true } : msg
          ));
        }
      }
    };
    }; // end connect()

    connect();

    return () => {
      wsShouldRunRef.current = false;
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      reconnectAttemptsRef.current = 0;
      const ws = wsRef.current;
      if (ws) {
        ws.onclose = null; // prevent reconnect on deliberate unmount
        ws.onerror = null;
        ws.close();
      }
      wsRef.current = null;
    };
  }, [loadConversations]);

  // Revoke any pending preview object URL on unmount.
  useEffect(() => {
    return () => {
      if (pendingFile?.previewUrl) URL.revokeObjectURL(pendingFile.previewUrl);
    };
  }, [pendingFile]);

  // ── Auto-scroll messages ───────────────────────────────────────────────

  const scrollToBottom = useCallback(() => {
    const el = chatMessagesRef.current;
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, typingUser, scrollToBottom]);

  // ── Initial load ───────────────────────────────────────────────────────

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  // ── Fetch avatar photos for conversation users ──────────────────────

  useEffect(() => {
    if (!conversations.length) return;
    const userIds = conversations
      .map(c => c.other_user?.id)
      .filter(id => id && !avatarPhotos[id]);
    if (!userIds.length) return;

    userIds.forEach(id => {
      axios.get(`${API_URL}/users/${id}/avatar`, {
        ...getAxiosConfig(),
        responseType: 'blob',
      }).then(res => {
        const url = URL.createObjectURL(res.data);
        setAvatarPhotos(prev => ({ ...prev, [id]: url }));
      }).catch(() => {}); // No photo available — keep initial
    });
  }, [conversations, API_URL]);

  // ── Auto-open conversation from navigation state (e.g. coach search) ──

  const handledTargetRef = useRef(false);

  useEffect(() => {
    if (handledTargetRef.current || !conversationsLoaded) return;
    const targetUser = location.state?.targetUser;
    if (!targetUser) return;

    handledTargetRef.current = true;
    startConversation(targetUser);
  }, [conversationsLoaded, location.state]);

  // ── Select conversation ────────────────────────────────────────────────

  const selectConversation = (conv) => {
    setSelectedConversation(conv);
    setSearchQuery('');
    setSearchResults([]);
    setTypingUser(null);
    loadMessages(conv.id);  // marks read server-side (GET messages defaults mark_read=true)

    // Optimistically clear this conversation's unread badge in the list — the
    // server marks the messages read on open, but nothing else refreshes the
    // local unread_count, so the badge would otherwise linger until reload.
    if (conv.unread_count) {
      setConversations(prev => prev.map(c => c.id === conv.id ? { ...c, unread_count: 0 } : c));
      setConvSearchResults(prev => prev ? prev.map(c => c.id === conv.id ? { ...c, unread_count: 0 } : c) : prev);
    }

    // Send mark_read via WS (drives read receipts to the other participant)
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: 'mark_read', conversation_id: conv.id }));
    }
  };

  // ── Mute / unmute conversation ──────────────────────────────────────────

  const toggleMute = async (conv, e) => {
    if (e) e.stopPropagation();
    if (!conv) return;
    const nextMuted = !conv.is_muted;
    // Optimistic local update for both the list and the selected conversation.
    setConversations(prev => prev.map(c => c.id === conv.id ? { ...c, is_muted: nextMuted } : c));
    setSelectedConversation(prev => (prev && prev.id === conv.id) ? { ...prev, is_muted: nextMuted } : prev);
    setConvSearchResults(prev => prev ? prev.map(c => c.id === conv.id ? { ...c, is_muted: nextMuted } : c) : prev);
    try {
      if (nextMuted) {
        await axios.post(`${MESSAGING_URL}/conversations/${conv.id}/mute`, null, getAxiosConfig());
      } else {
        await axios.delete(`${MESSAGING_URL}/conversations/${conv.id}/mute`, getAxiosConfig());
      }
    } catch (error) {
      console.error('Error toggling mute:', error);
      // Revert on failure.
      setConversations(prev => prev.map(c => c.id === conv.id ? { ...c, is_muted: conv.is_muted } : c));
      setSelectedConversation(prev => (prev && prev.id === conv.id) ? { ...prev, is_muted: conv.is_muted } : prev);
      setConvSearchResults(prev => prev ? prev.map(c => c.id === conv.id ? { ...c, is_muted: conv.is_muted } : c) : prev);
    }
  };

  // ── Search existing conversations (debounced) ───────────────────────────

  useEffect(() => {
    const q = convSearchQuery.trim();
    if (!q) {
      // Invalidate any in-flight request so it can't land after clearing.
      convSearchSeqRef.current++;
      setConvSearchResults(null);
      return;
    }
    // Sequence guard: a slow earlier request must not overwrite newer results,
    // and a fetch resolving after unmount must be a no-op.
    const seq = ++convSearchSeqRef.current;
    const handle = setTimeout(async () => {
      try {
        const response = await axios.get(
          `${MESSAGING_URL}/conversations/search?q=${encodeURIComponent(q)}`,
          getAxiosConfig()
        );
        if (seq !== convSearchSeqRef.current) return; // stale / unmounted
        const data = response.data;
        const results = Array.isArray(data) ? data : (data.conversations || []);
        // The search endpoint omits is_muted; merge it from already-loaded
        // conversations by id so the mute icon + toggle behave correctly.
        const loaded = conversationsRef.current;
        const merged = results.map(r => {
          const match = loaded.find(c => c.id === r.id);
          return { ...r, is_muted: match ? match.is_muted : (r.is_muted ?? false) };
        });
        setConvSearchResults(merged);
      } catch (error) {
        if (seq !== convSearchSeqRef.current) return; // stale / unmounted
        console.error('Error searching conversations:', error);
        setConvSearchResults([]);
      }
    }, 300);
    return () => clearTimeout(handle);
  }, [convSearchQuery, MESSAGING_URL]);

  // ── Search users ───────────────────────────────────────────────────────

  const searchUsers = async () => {
    if (!searchQuery.trim()) return;
    try {
      const response = await axios.get(
        `${MESSAGING_URL}/users/search?query=${encodeURIComponent(searchQuery)}`,
        getAxiosConfig()
      );
      setSearchResults(response.data.users || []);
    } catch (error) {
      console.error('Error searching users:', error);
    }
  };

  // ── Start new conversation ─────────────────────────────────────────────

  const startConversation = (targetUser) => {
    // Check if we already have a conversation with this user
    const existing = conversations.find(c => c.other_user?.id === targetUser.id);
    if (existing) {
      setPendingTarget(null);
      selectConversation(existing);
      return;
    }

    // Open chat panel for this user — conversation created on first send
    setPendingTarget(targetUser);
    setSelectedConversation(null);
    setMessages([]);
    setSearchQuery('');
    setSearchResults([]);
  };

  // ── Send message ───────────────────────────────────────────────────────

  const sendMessage = async () => {
    if (!newMessage.trim() && !pendingFile) return;
    // Re-entrancy guard: a rapid second click/Enter must not upload + send twice.
    if (isSendingRef.current) return;
    isSendingRef.current = true;
    setSending(true);
    try {
      // If pending target, create conversation via REST first.
      // (A new conversation requires a text message; attachments are only
      //  supported once the conversation exists.)
      if (pendingTarget) {
        if (!newMessage.trim()) {
          alert('Add a message to start the conversation, then attach a file.');
          return;
        }
        // An attachment can't ride along on a brand-new conversation; tell the
        // user it will be skipped and drop it so its object URL doesn't leak.
        if (pendingFile) {
          alert('Attachments can only be sent after the conversation exists. The message will be sent without the attachment.');
          removePendingFile();
        }
        try {
          const response = await axios.post(
            `${MESSAGING_URL}/conversations?target_user_id=${pendingTarget.id}&message=${encodeURIComponent(newMessage)}`,
            null,
            getAxiosConfig()
          );
          const convId = response.data.conversation_id;
          setPendingTarget(null);
          setNewMessage('');
          await loadConversations();
          setSelectedConversation({ id: convId, other_user: pendingTarget });
          loadMessages(convId);
        } catch (error) {
          const detail = error.response?.data?.detail || 'Failed to send message';
          alert(detail);
        }
        return;
      }

      // Existing conversation — send via WebSocket
      const ws = wsRef.current;
      if (!selectedConversation) return;

      if (!ws || ws.readyState !== WebSocket.OPEN) {
        alert('Connection lost. Please refresh the page.');
        return;
      }

      // Deferred attachment upload: upload now, then dispatch with the URL.
      if (pendingFile) {
        let uploaded;
        try {
          uploaded = await uploadPendingFile();
        } catch (error) {
          console.error('Error uploading file:', error);
          const detail = error.response?.data?.detail || 'Failed to upload file';
          alert(detail);
          return;
        }
        ws.send(JSON.stringify({
          type: 'message',
          conversation_id: selectedConversation.id,
          content: newMessage || '',
          attachment_url: uploaded.url,
          attachment_type: uploaded.type,
        }));
        removePendingFile();
        setNewMessage('');
        return;
      }

      ws.send(JSON.stringify({
        type: 'message',
        conversation_id: selectedConversation.id,
        content: newMessage
      }));

      setNewMessage('');
    } finally {
      isSendingRef.current = false;
      setSending(false);
    }
  };

  // ── Insert a template into the composer ────────────────────────────────

  const handleInsertTemplate = async (template) => {
    const other = selectedConversation?.other_user || pendingTarget;
    const fullName = (other?.name || '').trim();
    const nameParts = fullName ? fullName.split(/\s+/) : [];
    const firstName = nameParts[0] || '';
    const lastName = nameParts.length > 1 ? nameParts[nameParts.length - 1] : '';

    const values = {
      firstName,
      lastName,
      school: other?.school || other?.school_name || '',
      pos: other?.position || other?.pos || '',
      classYear: other?.class_year || other?.grad_year || '',
      coachName: (user?.first_name || user?.name || '').split(/\s+/)[0] || '',
    };

    const rendered = (template.parts || [])
      .map((p) => {
        if (p.kind === 'text') return p.value;
        const v = values[p.value];
        // Unknown token → drop it cleanly rather than printing the raw key.
        return v != null && v !== '' ? v : '';
      })
      .join('')
      // Dropped tokens can leave double spaces; collapse and trim.
      .replace(/ {2,}/g, ' ')
      .trim();

    setNewMessage(rendered);

    try {
      await axios.post(`${API_URL}/coach-templates/${template.id}/use`, null, getAxiosConfig());
      setTemplatesReloadKey((k) => k + 1);
    } catch (err) {
      console.error('Error marking template used:', err);
    }
  };

  // ── Typing indicator ──────────────────────────────────────────────────

  const handleTyping = () => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN || !selectedConversation) return;

    const now = Date.now();
    if (now - lastTypingSentRef.current < 2000) return; // Debounce 2s
    lastTypingSentRef.current = now;

    ws.send(JSON.stringify({
      type: 'typing',
      conversation_id: selectedConversation.id
    }));
  };

  // ── Attachment: select → preview → deferred upload on send ──────────────

  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB

  const handleFileSelected = () => {
    const file = fileInputRef.current?.files[0];
    if (!file) return;

    if (file.size > MAX_FILE_SIZE) {
      alert('File exceeds 10 MB limit. Try sending a link instead.');
      fileInputRef.current.value = '';
      return;
    }

    // Discard any prior pending preview.
    if (pendingFile?.previewUrl) URL.revokeObjectURL(pendingFile.previewUrl);

    const isImage = file.type.startsWith('image/');
    setPendingFile({
      file,
      isImage,
      previewUrl: URL.createObjectURL(file),
    });
    // Allow re-selecting the same file later.
    fileInputRef.current.value = '';
  };

  const removePendingFile = () => {
    if (pendingFile?.previewUrl) URL.revokeObjectURL(pendingFile.previewUrl);
    setPendingFile(null);
  };

  /** Upload the pending file and return { url, type }, or null on failure. */
  const uploadPendingFile = async () => {
    if (!pendingFile) return null;
    const formData = new FormData();
    formData.append('file', pendingFile.file);
    const response = await axios.post(`${MESSAGING_URL}/upload`, formData, {
      headers: {
        ...getAxiosConfig().headers,
        'Content-Type': 'multipart/form-data',
      },
    });
    return { url: response.data.url, type: response.data.type };
  };

  // ── Helpers for chat header ───────────────────────────────────────────

  const handleViewProfile = async () => {
    const other = selectedConversation?.other_user || pendingTarget;
    if (!other) return;

    if (other.role === 'coach') {
      // Coaches have a dedicated CoachProfile with school/position fields
      const config = { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } };
      try {
        const res = await axios.get(`${API_URL}/coaches/${other.id}/profile`, config);
        setCoachProfileModal(res.data);
      } catch (err) {
        console.log('Could not load coach profile:', err);
      }
    } else if (canViewAthletePage) {
      // Non-coach partner → canonical athlete-detail page, but only for viewers
      // the route allows (else RoleProtectedRoute would eject them to /dashboard).
      navigate(`/athletes/${other.id}`);
    }
  };

  const handleBack = () => {
    setSelectedConversation(null);
    setPendingTarget(null);
    setMessages([]);
  };

  const handleDeleteConversation = async () => {
    if (!selectedConversation) return;
    if (!window.confirm('Delete this conversation? You can still receive new messages from this person.')) return;
    try {
      await axios.delete(
        `${MESSAGING_URL}/conversations/${selectedConversation.id}`,
        getAxiosConfig()
      );
      setConversations(prev => prev.filter(c => c.id !== selectedConversation.id));
      setSelectedConversation(null);
      setMessages([]);
    } catch (error) {
      console.error('Error deleting conversation:', error);
    }
  };

  // ── Flag message ───────────────────────────────────────────────────

  const handleFlagSubmit = async () => {
    if (!flagModal) return;
    try {
      const params = new URLSearchParams();
      if (flagReason.trim()) params.set('reason', flagReason.trim());
      await axios.post(
        `${MESSAGING_URL}/messages/${flagModal.messageId}/flag?${params.toString()}`,
        null,
        getAxiosConfig()
      );
      alert('Message has been flagged for admin review.');
    } catch (error) {
      const detail = error.response?.data?.detail || 'Failed to flag message';
      alert(detail);
    }
    setFlagModal(null);
    setFlagReason('');
    setMenuMessageId(null);
  };

  // ── Block user ───────────────────────────────────────────────────

  const handleBlockUser = async () => {
    if (!blockConfirm) return;
    try {
      await axios.post(
        `${MESSAGING_URL}/users/${blockConfirm.userId}/block`,
        null,
        getAxiosConfig()
      );
      // Remove conversation from list and close chat
      setConversations(prev => prev.filter(c => c.other_user?.id !== blockConfirm.userId));
      setSelectedConversation(null);
      setPendingTarget(null);
      setMessages([]);
      alert(`${blockConfirm.name} has been blocked.`);
    } catch (error) {
      const detail = error.response?.data?.detail || 'Failed to block user';
      alert(detail);
    }
    setBlockConfirm(null);
    setMenuMessageId(null);
  };

  // ── Determine group positions for message bubbles ───────────────────

  const getGroupPosition = (messages, idx) => {
    const msg = messages[idx];
    const prev = idx > 0 ? messages[idx - 1] : null;
    const next = idx < messages.length - 1 ? messages[idx + 1] : null;
    const samePrev = prev && prev.is_mine === msg.is_mine;
    const sameNext = next && next.is_mine === msg.is_mine;
    if (samePrev && sameNext) return 'group-middle';
    if (samePrev && !sameNext) return 'group-bottom';
    if (!samePrev && sameNext) return 'group-top';
    return 'group-single';
  };

  // ── Render ─────────────────────────────────────────────────────────────

  const chatOpen = !!(selectedConversation || pendingTarget);
  const otherUser = selectedConversation?.other_user || pendingTarget;
  const otherName = otherUser?.name || 'Conversation';

  // Conversation list: search results take precedence, then filter pills apply.
  const baseConversations = convSearchResults !== null ? convSearchResults : conversations;
  const displayedConversations = baseConversations.filter(conv => {
    if (convFilter === 'unread') return conv.unread_count > 0;
    if (convFilter === 'personal') return PERSONAL_ROLES.includes(conv.other_user?.role);
    if (convFilter === 'newsletter') return !PERSONAL_ROLES.includes(conv.other_user?.role);
    return true; // 'all'
  });

  const FILTER_PILLS = [
    { key: 'all', label: 'All' },
    { key: 'unread', label: 'Unread' },
    { key: 'personal', label: 'Personal' },
    { key: 'newsletter', label: 'Newsletter' },
  ];

  const templatesNode = canUseTemplates ? (
    <TemplatesPanel
      onInsert={(t) => { setTemplatesPop(false); handleInsertTemplate(t); }}
      onManage={() => { setTemplatesPop(false); setTemplateModal('library'); }}
      onNew={() => { setTemplatesPop(false); setTemplateModal('new'); }}
      reloadKey={templatesReloadKey}
    />
  ) : null;

  const page = (
    <div>
      {!railView && (
        <div className="page-header">
          <h1 className="page-title">Messages</h1>
        </div>
      )}
      <div className={`messaging-layout${railView ? ' msg-grid' : ''}${chatOpen ? ' messaging-layout--chat-open' : ''}`}>
      <div className="messaging-sidebar">
        {railView && (
          <div className="ls-index__head msg-head">
            <h2 className="ls-index__title">Messages</h2>
            {canInitiate && (
              <button type="button" className="ls-btn ls-btn--round ls-btn--dark" aria-label="New message"
                aria-expanded={pickerOpen} onClick={() => setPickerOpen((v) => !v)}>+</button>
            )}
          </div>
        )}
        {!railView && !wsConnected && (
          <div className="messaging-sidebar__header">
            <span style={{ fontSize: '11px', color: '#ef4444' }}>Disconnected</span>
          </div>
        )}

        {canInitiate && (!railView || pickerOpen) && (
          <div className="messaging-sidebar__search">
            <input
              type="text"
              placeholder="Search users to message..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && searchUsers()}
              aria-label="Search users"
            />
            {searchResults.length > 0 && (
              <div className="messaging-search-results">
                {searchResults.map(u => (
                  <div
                    key={u.id}
                    className="conversation-item"
                    onClick={() => startConversation(u)}
                  >
                    <div className="conversation-item__avatar conversation-item__avatar--sm" style={{ background: getAvatarColor(u.name) }}>
                      {(u.name || '?')[0].toUpperCase()}
                    </div>
                    <div className="conversation-item__content">
                      <div className="conversation-item__name">{u.name}</div>
                      <div className="conversation-item__preview">{friendlyRole(u.role)}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="messaging-sidebar__search">
          <input
            type="text"
            placeholder="Search conversations..."
            value={convSearchQuery}
            onChange={(e) => setConvSearchQuery(e.target.value)}
            aria-label="Search conversations"
          />
        </div>

        <div className="conversation-filter-pills" style={{ display: 'flex', gap: '6px', padding: '0 12px 8px', flexWrap: 'wrap' }}>
          {FILTER_PILLS.map(pill => (
            <button
              key={pill.key}
              type="button"
              className={`conversation-filter-pill${convFilter === pill.key ? ' conversation-filter-pill--active' : ''}`}
              onClick={() => setConvFilter(pill.key)}
              aria-pressed={convFilter === pill.key}
              style={railView ? undefined : {
                fontSize: '12px',
                padding: '4px 10px',
                borderRadius: '999px',
                border: '1px solid #e2e8f0',
                cursor: 'pointer',
                background: convFilter === pill.key ? '#0066CC' : '#fff',
                color: convFilter === pill.key ? '#fff' : '#475569',
                fontWeight: convFilter === pill.key ? 600 : 400,
              }}
            >
              {pill.label}
            </button>
          ))}
        </div>

        <div
          className="conversation-list"
          onScroll={(e) => {
            const el = e.currentTarget;
            if (
              convHasMore &&
              !loadingMoreConvsRef.current &&
              convSearchResults === null &&
              el.scrollHeight - el.scrollTop - el.clientHeight < 300
            ) {
              loadMoreConversations();
            }
          }}
        >
          {displayedConversations.map(conv => {
            const name = conv.other_user?.name || 'Unknown';
            const unread = conv.unread_count > 0;
            return (
              <div
                key={conv.id}
                className={`conversation-item${selectedConversation?.id === conv.id ? ' conversation-item--active' : ''}${unread ? ' conversation-item--unread' : ''}`}
                onClick={() => selectConversation(conv)}
              >
                {avatarPhotos[conv.other_user?.id] ? (
                  <img className="conversation-item__avatar conversation-item__avatar--photo" src={avatarPhotos[conv.other_user.id]} alt="" />
                ) : (
                  <div className="conversation-item__avatar" style={{ background: getAvatarColor(name) }}>
                    {name[0].toUpperCase()}
                  </div>
                )}
                <div className="conversation-item__content">
                  <div className="conversation-item__top-row">
                    <span className="conversation-item__name">
                      {name}
                      {conv.is_muted && (
                        <svg
                          className="conversation-item__muted-icon"
                          width="13" height="13" viewBox="0 0 24 24" fill="none"
                          stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                          style={{ marginLeft: '5px', verticalAlign: 'middle' }}
                          aria-label="Muted"
                        >
                          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                          <path d="M18.63 13A17.89 17.89 0 0 1 18 8" />
                          <path d="M6.26 6.26A5.86 5.86 0 0 0 6 8c0 7-3 9-3 9h14" />
                          <path d="M18 8a6 6 0 0 0-9.33-5" />
                          <line x1="1" y1="1" x2="23" y2="23" />
                        </svg>
                      )}
                    </span>
                    <span className="conversation-item__time">{timeAgo(conv.last_message_at)}</span>
                  </div>
                  <div className="conversation-item__bottom-row">
                    <span className="conversation-item__preview">{conv.last_message}</span>
                    {unread && (
                      <span className="conversation-item__badge">{conv.unread_count}</span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
          {displayedConversations.length === 0 && (
            <div className="chat-empty chat-empty--sidebar">
              <div className="chat-empty__icon">💬</div>
              <div className="chat-empty__text">
                {convSearchResults !== null
                  ? 'No conversations match your search'
                  : (conversations.length === 0 ? 'No conversations yet' : 'No conversations in this filter')}
              </div>
            </div>
          )}
          {convLoadingMore && (
            <div style={{ padding: '12px', textAlign: 'center', color: '#94a3b8', fontSize: '12px' }}>
              Loading more…
            </div>
          )}
        </div>
      </div>

      <div className="chat-area">
        {railView && !wsConnected && <div className="ls-banner" role="status">Reconnecting…</div>}
        {chatOpen ? (
          <>
            <div className="chat-header">
              <button className="chat-header__back" onClick={handleBack} aria-label="Back to conversations">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="15 18 9 12 15 6" />
                </svg>
              </button>
              {avatarPhotos[otherUser?.id] ? (
                <img className="chat-header__avatar chat-header__avatar--photo" src={avatarPhotos[otherUser.id]} alt="" />
              ) : (
                <div className="chat-header__avatar" style={{ background: getAvatarColor(otherName) }}>
                  {otherName[0].toUpperCase()}
                </div>
              )}
              <div className="chat-header__info">
                <div className="chat-header__name">{otherName}</div>
                <div className="chat-header__status">{wsConnected ? 'Online' : 'Offline'}</div>
              </div>
              {otherUser?.id && (otherUser.role === 'coach' || canViewAthletePage) && (
                <Button variant="secondary" size="sm" className={railView ? 'msg-soft' : undefined} onClick={handleViewProfile}>
                  View Profile
                </Button>
              )}
              {!railView && (
              <>
              {selectedConversation && (
                <Button
                  variant="ghost"
                  iconOnly
                  onClick={(e) => toggleMute(selectedConversation, e)}
                  aria-label={selectedConversation.is_muted ? 'Unmute conversation' : 'Mute conversation'}
                  title={selectedConversation.is_muted ? 'Unmute' : 'Mute'}
                >
                  {selectedConversation.is_muted ? (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                      <path d="M18.63 13A17.89 17.89 0 0 1 18 8" />
                      <path d="M6.26 6.26A5.86 5.86 0 0 0 6 8c0 7-3 9-3 9h14" />
                      <path d="M18 8a6 6 0 0 0-9.33-5" />
                      <line x1="1" y1="1" x2="23" y2="23" />
                    </svg>
                  ) : (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                      <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                    </svg>
                  )}
                </Button>
              )}
              {otherUser?.id && (
                <Button
                  variant="ghost"
                  iconOnly
                  onClick={() => setBlockConfirm({ userId: otherUser.id, name: otherName })}
                  aria-label="Block user"
                  title="Block User"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"/>
                    <line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/>
                  </svg>
                </Button>
              )}
              {selectedConversation && (
                <Button variant="danger" iconOnly onClick={handleDeleteConversation} aria-label="Delete conversation">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="3 6 5 6 21 6" />
                    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                    <path d="M10 11v6" />
                    <path d="M14 11v6" />
                  </svg>
                </Button>
              )}
              </>
              )}
              {railView && (
                <div className="ls-menu-wrap">
                  <button type="button" className="ls-btn ls-btn--round" aria-label="More actions" aria-expanded={headerMenuOpen}
                    onClick={() => setHeaderMenuOpen((v) => !v)}>⋯</button>
                  {headerMenuOpen && (
                    <div className="ls-menu" role="menu">
                      {selectedConversation && (
                        <button type="button" role="menuitem" className="ls-menu__item"
                          onClick={(e) => { setHeaderMenuOpen(false); toggleMute(selectedConversation, e); }}>
                          {selectedConversation.is_muted ? 'Unmute' : 'Mute'}
                        </button>
                      )}
                      {otherUser?.id && (
                        <button type="button" role="menuitem" className="ls-menu__item"
                          onClick={() => { setHeaderMenuOpen(false); setBlockConfirm({ userId: otherUser.id, name: otherName }); }}>
                          Block
                        </button>
                      )}
                      {selectedConversation && (
                        <button type="button" role="menuitem" className="ls-menu__item ls-menu__item--danger"
                          onClick={() => { setHeaderMenuOpen(false); handleDeleteConversation(); }}>
                          Delete conversation
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="chat-messages" ref={chatMessagesRef}>
              {messages.map((msg, idx) => {
                const pos = getGroupPosition(messages, idx);
                const isLast = pos === 'group-bottom' || pos === 'group-single';
                const prevDiff = idx > 0 && messages[idx - 1].is_mine !== msg.is_mine;
                const gapClass = (idx === 0 || prevDiff) ? ' chat-bubble-row--gap' : '';
                return (
                  <div key={msg.id || idx} className={`chat-bubble-row${gapClass}`}>
                    <div
                      className={`chat-bubble ${msg.is_mine ? 'chat-bubble--mine' : 'chat-bubble--theirs'} chat-bubble--${pos}`}
                      style={{ position: 'relative' }}
                      onMouseEnter={() => { if (!msg.is_mine) setMenuMessageId(msg.id); }}
                      onMouseLeave={(e) => {
                        // Only close if mouse truly left the bubble+menu area
                        if (!e.currentTarget.contains(e.relatedTarget)) setMenuMessageId(null);
                      }}
                    >
                      {msg.content && <div>{msg.content}</div>}
                      {msg.attachment_url && (
                        <div style={{ marginTop: '8px' }}>
                          {msg.attachment_type === 'image' ? (
                            <img src={proxyUrl(msg.attachment_url)} style={{ maxWidth: '300px', borderRadius: '8px', cursor: 'zoom-in' }} alt="attachment" onLoad={() => setTimeout(scrollToBottom, 50)} onClick={(e) => { e.stopPropagation(); setFullScreenImage(proxyUrl(msg.attachment_url)); }} />
                          ) : (
                            <video src={proxyUrl(msg.attachment_url)} controls style={{ maxWidth: '300px', borderRadius: '8px' }} onLoadedData={() => setTimeout(scrollToBottom, 50)} />
                          )}
                        </div>
                      )}
                      {isLast && (
                        <div className="chat-bubble__time">
                          {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          {msg.is_mine && msg.is_read && ' ✓✓'}
                        </div>
                      )}
                      {!msg.is_mine && menuMessageId === msg.id && (
                        <div className="msg-action-menu">
                          <Button
                            variant="ghost"
                            style={{ display: 'block', width: '100%', textAlign: 'left' }}
                            onClick={(e) => { e.stopPropagation(); setFlagModal({ messageId: msg.id }); setMenuMessageId(null); }}
                          >
                            Flag Message
                          </Button>
                          <Button
                            variant="ghost"
                            style={{ display: 'block', width: '100%', textAlign: 'left' }}
                            onClick={(e) => { e.stopPropagation(); setBlockConfirm({ userId: msg.sender_id, name: msg.sender_name || otherName }); setMenuMessageId(null); }}
                          >
                            Block User
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
              {typingUser && (
                <div className="chat-bubble-row chat-bubble-row--gap">
                  <div className="typing-indicator">
                    <span className="typing-indicator__dot"></span>
                    <span className="typing-indicator__dot"></span>
                    <span className="typing-indicator__dot"></span>
                  </div>
                </div>
              )}
              {sending && (
                <div className="chat-bubble-row chat-bubble-row--gap">
                  <div
                    className="chat-bubble chat-bubble--mine chat-bubble--group-single chat-bubble--sending"
                    role="status"
                    aria-live="polite"
                  >
                    Sending&hellip;
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {canUseTemplates && !railView && (
              <TemplatesRail
                onInsert={handleInsertTemplate}
                onSeeAll={() => setTemplateModal('library')}
                onNew={() => setTemplateModal('new')}
                reloadKey={templatesReloadKey}
              />
            )}

            {pendingFile && (
              <div
                className="attachment-preview"
                style={{
                  display: 'flex', alignItems: 'center', gap: '10px',
                  padding: '8px 12px', borderTop: '1px solid #e2e8f0', background: '#f8fafc',
                }}
              >
                <div style={{ position: 'relative', display: 'inline-block' }}>
                  {pendingFile.isImage ? (
                    <img
                      src={pendingFile.previewUrl}
                      alt="Attachment preview"
                      style={{ width: '48px', height: '48px', objectFit: 'cover', borderRadius: '6px' }}
                    />
                  ) : (
                    <video
                      src={pendingFile.previewUrl}
                      style={{ width: '48px', height: '48px', objectFit: 'cover', borderRadius: '6px', background: '#000' }}
                    />
                  )}
                  <Button
                    variant="ghost"
                    iconOnly
                    onClick={removePendingFile}
                    aria-label="Remove attachment"
                    title="Remove attachment"
                    style={{ position: 'absolute', top: '-8px', right: '-8px' }}
                  >
                    &times;
                  </Button>
                </div>
                <span style={{ display: 'block', fontSize: '13px', color: '#475569', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {pendingFile.file.name}
                </span>
              </div>
            )}

            <div className="chat-input-bar">
              {railView && canUseTemplates && (
                <div className="msg-tpl-pop">
                  <button type="button" className="ls-btn ls-btn--tiny msg-tpl-pill" aria-expanded={templatesPop}
                    onClick={() => setTemplatesPop((v) => !v)}>Templates</button>
                  {templatesPop && <div className="adm__card msg-tpl-pop__panel">{templatesNode}</div>}
                </div>
              )}
              <input
                type="file"
                ref={fileInputRef}
                accept="image/*,video/*"
                style={{ display: 'none' }}
                onChange={handleFileSelected}
              />
              <Button
                variant="ghost"
                iconOnly
                onClick={() => fileInputRef.current?.click()}
                aria-label="Attach file"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                </svg>
              </Button>
              <input
                type="text"
                placeholder="Type a message..."
                value={newMessage}
                onChange={(e) => { setNewMessage(e.target.value); handleTyping(); }}
                onKeyDown={(e) => { if (e.key === 'Enter' && !sending) sendMessage(); }}
                aria-label="Type a message"
              />
              <Button
                variant="accent"
                iconOnly
                onClick={sendMessage}
                disabled={sending}
                aria-busy={sending}
                aria-label={sending ? 'Sending message' : 'Send message'}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="22" y1="2" x2="11" y2="13" />
                  <polygon points="22 2 15 22 11 13 2 9 22 2" />
                </svg>
              </Button>
            </div>
          </>
        ) : (
          <div className="chat-empty">
            <div className="chat-empty__icon">💬</div>
            <div className="chat-empty__text">
              {canInitiate
                ? 'Select a conversation or search for a user to start messaging'
                : 'Select a conversation to view messages'}
            </div>
          </div>
        )}
      </div>

      {railView && (
        <ConversationContext
          other={chatOpen ? otherUser : null}
          isAthlete={isAthlete(otherUser?.role)}
          canOpenProfile={!!otherUser?.id && (otherUser.role === 'coach' || canViewAthletePage)}
          onOpenProfile={handleViewProfile}
          templates={templatesNode}
        />
      )}

      {coachProfileModal && (
        <div className="modal-overlay" role="dialog" aria-modal="true" onClick={() => setCoachProfileModal(null)}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="modal-panel" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '420px' }}>
            <button className="modal-close" onClick={() => setCoachProfileModal(null)}>&times;</button>
            <div style={{ textAlign: 'center', marginBottom: '16px' }}>
              {coachProfileModal.player_photo && (
                <img src={coachProfileModal.player_photo} alt="Coach" style={{
                  width: '80px', height: '80px', borderRadius: '50%', objectFit: 'cover', marginBottom: '12px',
                }} />
              )}
              <div className="modal-title">
                {coachProfileModal.user?.first_name} {coachProfileModal.user?.last_name}
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', fontSize: '14px' }}>
              {coachProfileModal.position_title && (
                <div><span style={{ color: '#64748b' }}>Title:</span> <strong>{coachProfileModal.position_title}</strong></div>
              )}
              {coachProfileModal.school_name && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ color: '#64748b' }}>School:</span>
                  {coachProfileModal.school_logo && <img src={coachProfileModal.school_logo} alt="" style={{ width: '24px', height: '24px', objectFit: 'contain', borderRadius: '4px' }} />}
                  <strong>{coachProfileModal.school_name}</strong>
                </div>
              )}
              {coachProfileModal.sport && (
                <div><span style={{ color: '#64748b' }}>Sport:</span> <strong>{coachProfileModal.sport}</strong></div>
              )}
              {coachProfileModal.division && (
                <div><span style={{ color: '#64748b' }}>Division:</span> <strong>{coachProfileModal.division}</strong></div>
              )}
              {(coachProfileModal.city || coachProfileModal.state) && (
                <div><span style={{ color: '#64748b' }}>Location:</span> <strong>{[coachProfileModal.city, coachProfileModal.state].filter(Boolean).join(', ')}</strong></div>
              )}
              {coachProfileModal.personal_statement && (
                <div style={{ marginTop: '8px', padding: '12px', background: '#f8fafc', borderRadius: '8px', lineHeight: 1.5 }}>
                  {coachProfileModal.personal_statement}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {flagModal && (
        <div className="modal-overlay" role="dialog" aria-modal="true" onClick={() => { setFlagModal(null); setFlagReason(''); }}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="modal-panel" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '400px' }}>
            <button className="modal-close" onClick={() => { setFlagModal(null); setFlagReason(''); }}>&times;</button>
            <div className="modal-title" style={{ marginBottom: '16px' }}>Flag Message</div>
            <p style={{ fontSize: '14px', color: '#64748b', marginBottom: '12px' }}>
              This message will be sent to admins for review.
            </p>
            <textarea
              placeholder="Reason (optional)"
              value={flagReason}
              onChange={(e) => setFlagReason(e.target.value)}
              rows={3}
              style={{
                width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #e2e8f0',
                fontSize: '14px', resize: 'vertical', marginBottom: '16px', boxSizing: 'border-box',
              }}
            />
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <Button variant="secondary" onClick={() => { setFlagModal(null); setFlagReason(''); }}>Cancel</Button>
              <Button variant="danger" onClick={handleFlagSubmit}>Flag</Button>
            </div>
          </div>
        </div>
      )}

      {blockConfirm && (
        <div className="modal-overlay" role="dialog" aria-modal="true" onClick={() => setBlockConfirm(null)}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="modal-panel" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '400px' }}>
            <button className="modal-close" onClick={() => setBlockConfirm(null)}>&times;</button>
            <div className="modal-title" style={{ marginBottom: '16px' }}>Block {blockConfirm.name}?</div>
            <p style={{ fontSize: '14px', color: '#64748b', marginBottom: '16px' }}>
              They will no longer be able to message you and this conversation will be hidden.
            </p>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <Button variant="secondary" onClick={() => setBlockConfirm(null)}>Cancel</Button>
              <Button variant="danger" onClick={handleBlockUser}>Block</Button>
            </div>
          </div>
        </div>
      )}

      {canUseTemplates && templateModal && (
        <div className="modal-overlay" role="dialog" aria-modal="true" onClick={() => setTemplateModal(null)}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="modal-panel" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '480px', width: '100%' }}>
            <button className="modal-close" onClick={() => setTemplateModal(null)}>&times;</button>
            {templateModal === 'library' ? (
              <TemplateLibrary
                onClose={() => setTemplateModal(null)}
                onChange={() => setTemplatesReloadKey((k) => k + 1)}
              />
            ) : (
              <TemplateEditor
                template={null}
                onSaved={() => { setTemplateModal(null); setTemplatesReloadKey((k) => k + 1); }}
                onCancel={() => setTemplateModal(null)}
              />
            )}
          </div>
        </div>
      )}

      {fullScreenImage && (
        <FullScreenImage url={fullScreenImage} onClose={() => setFullScreenImage(null)} />
      )}
    </div>
    </div>
  );
  return railView ? <RailFrame active="messages">{page}</RailFrame> : page;
}
